import {
  CreateCodesetInput,
  GetCodesetResponse,
  UpdateCodesetDto,
} from 'src/components/dataset/create-cohort/steps/codes-selection-components/code-sets.dto';
import { configuration } from 'src/configuration';
import { store } from 'src/store';
import { selectRootOrganizationId, selectDataSourceId } from 'src/store/create-cohort';
import { selectAuthToken } from 'src/store/selectors';
import { post, get, put } from 'src/utils/fetch';
import { to } from 'to-await';

const { accountApiUrl } = configuration;

export const getCodesetsbyDataSourceId = async ({
  token,
  dataSourceId,
  codesetId,
}: {
  token: string;
  dataSourceId: string;
  codesetId?: string;
}) => {
  const url = codesetId
    ? `${accountApiUrl}/data-sources/${dataSourceId}/codesets?codesetId=${codesetId}`
    : `${accountApiUrl}/data-sources/${dataSourceId}/codesets`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return await response.json();
};

export const getCodesets = async ({ token, codesetId }: { token: string; codesetId?: string }) => {
  const url = codesetId
    ? `${accountApiUrl}/data-sources/codesets?codesetId=${codesetId}`
    : `${accountApiUrl}/data-sources/codesets`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return await response.json();
};

export const getCodesetById = async ({
  token,
  codesetId,
}: {
  token: string;
  codesetId: string;
}) => {
  const response = await fetch(`${accountApiUrl}/codesets/${codesetId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return await response.json();
};

export async function createCodeset(input: CreateCodesetInput): Promise<GetCodesetResponse> {
  const idToken = selectAuthToken(store.getState());
  const url = `${configuration.accountApiUrl}/codesets`;

  const [error, response] = await to(
    post(url, input, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    }),
  );

  if (error) {
    throw new Error(error.message);
  }

  return response;
}

export async function getCodeset(id: string): Promise<GetCodesetResponse> {
  const state = store.getState();
  const idToken = selectAuthToken(state);

  const organizationId = selectRootOrganizationId(state);

  const datasourceId = selectDataSourceId(state);

  const url = `${configuration.accountApiUrl}/codesets/organizations/${organizationId}/datasources/${datasourceId}/codesets/${id}`;

  const [error, response] = await to(
    get(url, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    }),
  );

  if (error) {
    throw new Error(error.message);
  }

  return response;
}

export async function updateCodeset(
  organizationId: string,
  datasourceId: string,
  id: string,
  updateDto: UpdateCodesetDto,
): Promise<GetCodesetResponse> {
  const idToken = selectAuthToken(store.getState());
  const url = `${configuration.accountApiUrl}/codesets/organizations/${organizationId}/datasources/${datasourceId}/codesets/${id}`;

  const [error, response] = await to(
    put(url, updateDto, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    }),
  );

  if (error) {
    throw new Error(error.message);
  }

  return response;
}

export async function getCodesetsByOrganizationAndDatasource(
  organizationId: string,
  datasourceId: string,
): Promise<{
  codesets: GetCodesetResponse[];
  lastEvaluatedKey?: Record<string, any>;
}> {
  const idToken = selectAuthToken(store.getState());

  const url = `${configuration.accountApiUrl}/codesets/organizations/${organizationId}/datasources/${datasourceId}`;

  const [error, response] = await to(
    get(url, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    }),
  );

  if (error) {
    console.error(error);
    throw new Error(error.message);
  }

  return response;
}
